import type {
    co_getplato_grid_api_domain_DraftOrderLineItem,
    co_getplato_grid_api_domain_NormalDraftOrderLineItem,
    co_getplato_grid_api_domain_NoteDraftOrderLineItem,
    co_getplato_grid_api_domain_VariantStock,
    co_getplato_grid_sales_features_draft_create_Request,
    co_getplato_grid_sales_features_draft_findprice_Request,
    co_getplato_grid_sales_features_draft_lineitem_normal_update_Request,
    co_getplato_grid_sales_features_draft_lineitem_note_add_Request,
    co_getplato_grid_sales_features_draft_lineitem_note_delete_Request,
    co_getplato_grid_sales_features_draft_lineitem_note_update_Request,
    co_getplato_grid_sales_features_draft_submit_Request,
    co_getplato_grid_sales_features_draft_update_Request,
    co_getplato_grid_sales_features_inventory_config_Response,
    co_getplato_grid_sales_SalDraftOrder,
} from '@/lib/grid/api';
import { SrPartialPackingTypeMap } from '@plato/data/starrocks/types';
import type { ZodType } from 'zod';
import { z } from 'zod';
import type { RouterOutput } from './infer-trpc-types';

export const salesAddressSchema = z
    .object({
        address_id: z.string().optional(),
        street: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        country_code: z.string().optional(),
        line1: z.string().optional(),
        line2: z.string().optional(),
        line3: z.string().optional(),
        zip: z.string().optional(),
    })
    .refine((obj: Record<string | number | symbol, unknown>) => Object.values(obj).some(Boolean));

export const salesDraftTypeSchema = z.enum(['ORDER', 'QUOTE']);

export const coGetplatoGridSalesFeaturesDraftCreateRequestSchema = z.object({
    draft_order_id: z.string(),
    type: salesDraftTypeSchema,
    customer_id: z.string(),
    billing_address: salesAddressSchema.optional(),
    shipping_address: salesAddressSchema.optional(),
    contact_person_id: z.string().optional().default(''),
    contact_person_name: z.string().optional().default(''),
    due_date: z.string().optional(),
    delivery_date: z.string().optional(),
    shipping_type_id: z.string().optional(),
    storage_id: z.string(),
    notes: z.string().optional().default(''),
    external: z.record(z.string(), z.any()).optional(),
    created_by: z.string().optional(),
}) satisfies ZodType<Omit<co_getplato_grid_sales_features_draft_create_Request, 'created_by'>>;

//
export const createNormalLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    product_variant_id: z.string(),
    storage_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string().nullable(),
    shipping_type_id: z.string(),
});

export type CreateNormalLineItemInputDTO = z.infer<typeof createNormalLineItemInputSchema>;

// config
export const getProductVariantConfigSchema = z.object({
    productVariantId: z.string(),
});

export type GetProductVariantConfigDTO = z.infer<typeof getProductVariantConfigSchema>;

// infer TRPC types
type SalesDraftQueryRouterOutput = RouterOutput['platoUser']['query']['salesDraft'];
type SalesDraftCommandRouterOutput = RouterOutput['platoUser']['command']['salesDraft'];

export type CreateNormalLineItemResponse = SalesDraftCommandRouterOutput['lineItems']['createNormalLineItem'];

export type SalesDraftResponse = SalesDraftQueryRouterOutput['getSalesDraft']['byId'];
export type SalesDraft = SalesDraftResponse['draft_order'];
export type SalesDraftType = z.infer<typeof salesDraftTypeSchema>;
export type SalesDraftOrder = SalesDraft;
export type SalesDraftLineItems = SalesDraft['line_items'];
export type DraftMachineLineItem = SalesDraftLineItems[0];

export type SalesProductVariantConfig =
    SalesDraftQueryRouterOutput['getInventory']['getProductVariantConfig'];

// infer TRPC types

// extendes types

export type ExtendedNormalDraftOrderLineItem = co_getplato_grid_api_domain_NormalDraftOrderLineItem & {
    line_item_id: string;
    storage_name: string;
    external: Record<string, any>;
} & SalesDraftProductVariantDim;

export type ExtendedNoteDraftOrderLineItem = co_getplato_grid_api_domain_NoteDraftOrderLineItem & {
    line_item_id: string;
    external: Record<string, any>;
};

export type ExtendedSalesDraftLineItem = co_getplato_grid_api_domain_DraftOrderLineItem & {
    normal_line_item?: ExtendedNormalDraftOrderLineItem;
    note_line_item?: ExtendedNoteDraftOrderLineItem;
};

export type ExtendedSalesDraftOrder = co_getplato_grid_sales_SalDraftOrder & {
    type: SalesDraftType;
    line_items: ExtendedSalesDraftLineItem[];
    customer_name: string;
    storage_name: string;
};

export type ExtendedSalesDraftOrderItem = co_getplato_grid_sales_SalDraftOrder & {
    customer_name: string;
    storage_name: string;
};

export type ExtendedSalesDraftFindResponse = {
    draft_order: ExtendedSalesDraftOrder;
};

// product variant configs
export type ExtendedSalesProductVariantConfigStock = co_getplato_grid_api_domain_VariantStock & {
    storage_name: string;
    quantity_unit_sales: string | null;
};

export type ExtendedSalesProductVariantConfigResponse =
    co_getplato_grid_sales_features_inventory_config_Response & {
        stock: ExtendedSalesProductVariantConfigStock[];
    };

export type SalesDraftProductVariantDim = {
    product_variant_name: string;
    product_variant_id: string;
    sales_units_per_package_unit?: number | null;
    partial_packing_type?: SrPartialPackingTypeMap;
    price_scale_factor?: number;
};

export const salesDiscountSchema = z.object({
    code: z.string(),
    amount: z.number(),
    percent: z.number(),
});

//
export const updateNormalLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    draft_line_item_id: z.string(),
    product_variant_id: z.string(),
    storage_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string(),
    price: z.number(),
    rebate: z.array(salesDiscountSchema),
    shipping_type_id: z.string(),
    note: z.string(),
    external: z.record(z.string(), z.any()),
}) satisfies ZodType<co_getplato_grid_sales_features_draft_lineitem_normal_update_Request>;

export type UpdateNormalLineItemInputDTO = z.infer<typeof updateNormalLineItemInputSchema>;

export type UpdateNormalLineItemResponse = SalesDraftCommandRouterOutput['lineItems']['updateNormalLineItem'];

/// delete line item
export const deleteNormalLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    draft_line_item_id: z.string(),
});

export type DeleteNormalLineItemInputDTO = z.infer<typeof deleteNormalLineItemInputSchema>;

export type DeleteNormalLineItemResponse = SalesDraftCommandRouterOutput['lineItems']['deleteNormalLineItem'];

// draftFindDefaultheader
export const draftFindDefaultheaderInputSchema = z.object({
    sales_order_type: salesDraftTypeSchema,
    customer_id: z.string(),
    clerk_id: z.string(),
});

export type DraftFindDefaultheaderInputDTO = z.infer<typeof draftFindDefaultheaderInputSchema>;

export type DraftFindDefaultHeaderResponse = SalesDraftQueryRouterOutput['getSalesDraft']['getDefaultHeader'];

export const draftHeaderSchema = z.object({
    draft_order_id: z.string().min(1, 'Required'),
    type: salesDraftTypeSchema,
    shipping_address: salesAddressSchema,
    billing_address: salesAddressSchema,
    contact_person_id: z.string().optional().default(''),
    due_date: z.string().optional(),
    delivery_date: z.string().optional(),
    shipping_type_id: z.string().optional(),
    notes: z.string().optional().default(''),
    external: z.object({
        document_type: z.string().min(1, 'Required'),
        po_number: z.string().optional().default(''),
    }),
});

export const updateDraftOrderInputSchema = draftHeaderSchema.extend({
    created_by: z.string().optional(),
    contact_person_name: z.string().optional(),
    storage_id: z.string().optional(),
    release: z.boolean().optional(),
}) satisfies ZodType<Omit<co_getplato_grid_sales_features_draft_update_Request, 'created_by'>>;

export type UpdateDraftOrderInputDTO = z.input<typeof updateDraftOrderInputSchema>;

export type UpdateDraftOrderResponse = SalesDraftCommandRouterOutput['draft']['update'];

export type GetCustomerAddressesResponse = RouterOutput['platoUser']['getManyCustomerAddress']['get'];

export const submitDraftOrderInputSchema = z.object({
    draft_order_id: z.string(),
    clerk_id: z.string(),
}) satisfies ZodType<co_getplato_grid_sales_features_draft_submit_Request>;

export type SubmitDraftOrderInputDTO = z.infer<typeof submitDraftOrderInputSchema>;

export type SubmitDraftOrderResponse = SalesDraftCommandRouterOutput['draft']['submit'];

export const draftFindPriceInputSchema = z.object({
    product_variant_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string(),
    customer_id: z.string(),
    storage_id: z.string(),
    rebate: z.array(salesDiscountSchema),
    price: z.number().optional(),
}) satisfies ZodType<co_getplato_grid_sales_features_draft_findprice_Request>;

export const draftFindSalesConfigInputSchema = z.object({
    product_variant_id: z.string(),
});

// this is relaxed because of data issues in StarRocks causing validation failures
export const partialPackingTypeSchema = z.string() as z.ZodType<SrPartialPackingTypeMap>;

export const draftFindSalesConfigOutputSchema = z.object({
    factor_sales_unit: z.number().optional(),
    package_unit: z.string().optional(),
    partial_packing_type: partialPackingTypeSchema.optional(),
    price_scale_factor: z.number().optional(),
    product_variant_id: z.string(),
    quantity_unit_sales: z.string().optional(),
    quantity_unit_storage: z.string().optional(),
    sales_units_per_package_unit: z.number().optional(),
});

export type DraftFindPriceInputDTO = z.infer<typeof draftFindPriceInputSchema>;

export type DraftFindPriceResponse = SalesDraftQueryRouterOutput['getSalesDraft']['getPrice'];

export type DraftFindSalesConfigResponse = z.infer<typeof draftFindSalesConfigOutputSchema>;

export type DraftListGetFilterOptionsResponse =
    SalesDraftQueryRouterOutput['getSalesDraftList']['getFilterOptions'];

export const addNoteLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    note: z.string().min(1),
    external: z.record(z.string(), z.any()),
}) satisfies ZodType<
    Omit<co_getplato_grid_sales_features_draft_lineitem_note_add_Request, 'draft_line_item_id'>
>;

export type AddNoteLineItemInputDTO = z.infer<typeof addNoteLineItemInputSchema>;
export type AddNoteLineItemResponse = SalesDraftCommandRouterOutput['lineItems']['addNoteLineItem'];

export const updateNoteLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    draft_line_item_id: z.string(),
    note: z.string(),
    external: z.record(z.string(), z.any()),
}) satisfies ZodType<co_getplato_grid_sales_features_draft_lineitem_note_update_Request>;

export type UpdateNoteLineItemInputDTO = z.infer<typeof updateNoteLineItemInputSchema>;
export type UpdateNoteLineItemResponse = SalesDraftCommandRouterOutput['lineItems']['updateNoteLineItem'];

export const deleteNoteLineItemInputSchema = z.object({
    draft_line_item_id: z.string(),
    draft_order_id: z.string(),
}) satisfies ZodType<co_getplato_grid_sales_features_draft_lineitem_note_delete_Request>;

export type DeleteNoteLineItemInputDTO = z.infer<typeof deleteNoteLineItemInputSchema>;
export type DeleteNoteLineItemResponse = SalesDraftCommandRouterOutput['lineItems']['deleteNoteLineItem'];

export type SalesDraftHeaderExtraRequiredField = 'po_number' | 'contact_person_id';

export type DraftSalesOrderLineItem = PrismaJson.DraftSalesOrderLineItem[number];
